import React from 'react'
import { Box, Text, VStack, Flex } from '@chakra-ui/react'
import { Pie } from 'react-chartjs-2'
import {
  Chart as ChartJS,
  ArcElement,
  Tooltip,
  Legend,
  ChartOptions
} from 'chart.js'

ChartJS.register(ArcElement, Tooltip, Legend)

interface PieSlice {
  label: string
  value: number
  color: string
}

interface PieChartProps {
  data: PieSlice[]
  title: string
  size?: number
}

const PieChart: React.FC<PieChartProps> = ({ data, title, size = 200 }) => {
  // Filter out any data with 0 or negative values
  const filteredData = data.filter(slice => slice.value > 0)
  const total = filteredData.reduce((sum, slice) => sum + slice.value, 0)
  if (total === 0) return <Text>No data to display</Text>

  const chartData = {
    labels: filteredData.map(slice => slice.label),
    datasets: [
      {
        data: filteredData.map(slice => slice.value),
        backgroundColor: filteredData.map((slice, index) => {
          // Enhanced 3D-style colors with better saturation and depth
          const colorMap: { [key: string]: string } = {
            'Bitcoin': '#FF8533', // Primary brand orange
            'Stocks': '#33CCFF',  // Cyan accent
            'Bonds': '#10B981',   // Emerald green
            'Cash': '#FF33CC',    // Pink secondary
            'Real Estate': '#F59E0B', // Amber
            'Other Crypto': '#8B5CF6', // Purple
            'Other Assets': '#EF4444', // Red
            'Commodities': '#06B6D4', // Teal
            'Other': '#6B7280'    // Neutral gray
          }

          // Fallback enhanced palette
          const fallbackColors = [
            '#FF8533', '#33CCFF', '#FF33CC', '#10B981',
            '#8B5CF6', '#F59E0B', '#EF4444', '#06B6D4'
          ]

          return colorMap[slice.label] || fallbackColors[index % fallbackColors.length]
        }),
        borderColor: filteredData.map(() => 'rgba(255, 255, 255, 0.3)'),
        borderWidth: 2,
        hoverBorderColor: filteredData.map(() => 'rgba(255, 133, 51, 1)'),
        hoverBorderWidth: 3,
        hoverOffset: 10,
        shadowColor: 'rgba(0, 0, 0, 0.5)',
        shadowBlur: 20,
        shadowOffsetX: 0,
        shadowOffsetY: 8
      }
    ]
  }

  const options: ChartOptions<'pie'> = {
    responsive: true,
    maintainAspectRatio: false,
    plugins: {
      legend: {
        display: false // Hide default legend, we'll create custom HTML legend
      },
      tooltip: {
        backgroundColor: 'rgba(17, 24, 39, 0.96)',
        titleColor: '#F9FAFB',
        bodyColor: '#E5E7EB',
        borderColor: 'rgba(156, 163, 175, 0.2)',
        borderWidth: 1,
        cornerRadius: 8,
        displayColors: true,
        padding: 12,
        titleFont: {
          size: 14,
          weight: 'bold' as const,
          family: 'Inter, -apple-system, BlinkMacSystemFont, sans-serif'
        },
        bodyFont: {
          size: 13,
          family: 'Inter, -apple-system, BlinkMacSystemFont, sans-serif'
        },
        callbacks: {
          title: function(context) {
            return context[0].label || ''
          },
          label: function(context) {
            const value = context.parsed || 0
            const percentage = ((value / total) * 100).toFixed(1)
            return `${percentage}% • $${value.toLocaleString()}`
          }
        }
      }
    },
    animation: {
      animateScale: true,
      animateRotate: true,
      duration: 1000,
      easing: 'easeOutBounce'
    },
    elements: {
      arc: {
        borderWidth: 3,
        borderColor: 'rgba(255, 255, 255, 0.4)',
        hoverBorderWidth: 4,
        hoverBorderColor: 'rgba(255, 133, 51, 0.9)'
      }
    },
    onHover: (event, activeElements) => {
      if (event.native && event.native.target && 'style' in event.native.target) {
        (event.native.target as HTMLElement).style.cursor = activeElements.length > 0 ? 'pointer' : 'default'
      }
    }
  }

  // Create custom HTML legend
  const renderLegend = () => {
    return (
      <Box mt={4} px={4}>
        <VStack spacing={2} align="stretch">
          {filteredData.map((slice, index) => {
            const percentage = ((slice.value / total) * 100).toFixed(1)
            const bgColors = chartData.datasets[0].backgroundColor as string[]
            const color = bgColors[index]
            
            return (
              <Flex key={index} align="center" justify="flex-start">
                <Box
                  w="12px"
                  h="12px"
                  borderRadius="50%"
                  bg={color}
                  mr={3}
                  flexShrink={0}
                />
                <Text
                  fontSize="15px"
                  fontWeight="bold"
                  color="#D97706"
                  fontFamily="Inter, -apple-system, BlinkMacSystemFont, sans-serif"
                >
                  {slice.label} ({percentage}%)
                </Text>
              </Flex>
            )
          })}
        </VStack>
      </Box>
    )
  }

  return (
    <VStack spacing={6} align="center">
      <Text
        fontSize="lg"
        fontWeight="bold"
        color="#D97706"
        letterSpacing="tight"
      >
        {title}
      </Text>
      <Box 
        width={size} 
        height={size}
        p={4}
        filter="drop-shadow(0 10px 25px rgba(0, 0, 0, 0.4))"
        transform="perspective(1000px) rotateX(5deg)"
        sx={{
          transformStyle: 'preserve-3d'
        }}
      >
        <Pie data={chartData} options={options} />
      </Box>
      {renderLegend()}
    </VStack>
  )
}

export default PieChart
